export type Language = 'es' | 'en';

type Dictionary = {
  header: {
    studio: string;
    nav: { portfolio: string; about: string; styles: string };
    book: string;
    openMenu: string;
    closeMenu: string;
    languageLabel: string;
  };
  hero: {
    location: string;
    customTattoo: string;
    artist: string;
    titleLine1: string;
    titleLine2: string;
    subtitle: string;
    dm: string;
    form: string;
    backgroundAlt: string;
    recentLabel: string;
    recentTitle: string;
  };
  portfolio: {
    sectionLabel: string;
    titleLine1: string;
    titleLine2: string;
    empty: string;
    seeMore: string;
    divider: string;
    close: string;
    previous: string;
    next: string;
    viewPhoto: string;
    viewGallery: string;
    instagramAria: string;
  };
  about: {
    sectionLabel: string;
    titleLine1: string;
    titleLine2: string;
    paragraph1: string;
    paragraph2: string;
    specialties: string;
    specialtiesList: { name: string; description: string }[];
    instagram: string;
    cta: string;
  };
  stats: {
    sectionLabel: string;
    title: string;
    cards: { value: string; label: string; description: string }[];
    prevReview: string;
    nextReview: string;
    goToReview: string;
    emptyReviews: string;
  };
  booking: {
    badge: string;
    titleLine1: string;
    titleLine2: string;
    intro: string;
    trustItems: string[];
    socialProof: string;
    submittedTitle: string;
    submittedBody: string;
    continueDm: string;
    formTitle: string;
    formSubtitle: string;
    dmButton: string;
    noInstagram: string;
    fullName: string;
    fullNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    phone: string;
    phonePlaceholder: string;
    preferredDate: string;
    bodyPlacement: string;
    bodyPlacementPlaceholder: string;
    tattooIdea: string;
    tattooIdeaPlaceholder: string;
    references: string;
    optional: string;
    referencesPlaceholder: string;
    sending: string;
    submit: string;
    consent: string;
    bodyPlacementError: string;
    submitError: string;
  };
  location: {
    location: string;
    bookings: string;
    bookOnline: string;
  };
  footer: {
    location: string;
    nav: { portfolio: string; about: string; book: string };
  };
};

export const BODY_PLACEMENTS: { value: string; es: string; en: string }[] = [
  { value: 'forearm', es: 'Antebrazo', en: 'Forearm' },
  { value: 'full-arm', es: 'Brazo completo', en: 'Full arm' },
  { value: 'wrist', es: 'Muneca', en: 'Wrist' },
  { value: 'shoulder', es: 'Hombro', en: 'Shoulder' },
  { value: 'back', es: 'Espalda', en: 'Back' },
  { value: 'chest', es: 'Pecho', en: 'Chest' },
  { value: 'side', es: 'Costado', en: 'Ribcage' },
  { value: 'thigh', es: 'Muslo', en: 'Thigh' },
  { value: 'calf', es: 'Pantorrilla', en: 'Calf' },
  { value: 'ankle', es: 'Tobillo', en: 'Ankle' },
  { value: 'neck', es: 'Cuello', en: 'Neck' },
  { value: 'other', es: 'Otro', en: 'Other' },
];

export const STYLE_LABEL_TRANSLATIONS: Record<string, { es: string; en: string }> = {
  Realismo: { es: 'Realismo', en: 'Realism' },
  'Retratos de mascotas': { es: 'Retratos de mascotas', en: 'Pet portraits' },
  'Linea fina': { es: 'Linea fina', en: 'Fine line' },
  'Línea fina': { es: 'Línea fina', en: 'Fine line' },
  Color: { es: 'Color', en: 'Color' },
};

export const STYLE_TAG_TRANSLATIONS: Record<string, { es: string; en: string }> = {
  'Tonalidades grises': { es: 'Tonalidades grises', en: 'Black and grey' },
  Mascotas: { es: 'Mascotas', en: 'Pets' },
  'Fine line': { es: 'Fine line', en: 'Fine line' },
  'Color vibrante': { es: 'Color vibrante', en: 'Vibrant color' },
};

export const REVIEW_QUOTE_TRANSLATIONS: Record<string, { es: string; en: string }> = {
  'Juga captó exactamente lo que tenía en mente. El proceso fue calmado y profesional y mi tatuaje quedó mejor de lo que imaginé.':
    {
      es: 'Juga captó exactamente lo que tenía en mente. El proceso fue calmado y profesional y mi tatuaje quedó mejor de lo que imaginé.',
      en: 'Juga captured exactly what I had in mind. The process was calm and professional, and my tattoo turned out even better than I imagined.',
    },
  'Un artista con visión real. Tomó mi referencia vaga y la convirtió en algo que llevo con orgullo. Volveré sin duda.':
    {
      es: 'Un artista con visión real. Tomó mi referencia vaga y la convirtió en algo que llevo con orgullo. Volveré sin duda.',
      en: 'A true artist with vision. He took my vague reference and turned it into something I wear with pride. I will definitely come back.',
    },
  'Desde el primer boceto supe que estaba en buenas manos. La atención al detalle es increíble y el resultado superó todas mis expectativas.':
    {
      es: 'Desde el primer boceto supe que estaba en buenas manos. La atención al detalle es increíble y el resultado superó todas mis expectativas.',
      en: 'From the first sketch, I knew I was in good hands. The attention to detail is incredible, and the result exceeded all my expectations.',
    },
  'El ambiente del estudio es muy tranquilo y Juga hace que todo el proceso sea cómodo. El tatuaje quedó exactamente como lo soñé.':
    {
      es: 'El ambiente del estudio es muy tranquilo y Juga hace que todo el proceso sea cómodo. El tatuaje quedó exactamente como lo soñé.',
      en: 'The studio atmosphere is very calm, and Juga makes the whole process comfortable. The tattoo turned out exactly how I dreamed it.',
    },
};

export const TRANSLATIONS: Record<Language, Dictionary> = {
  es: {
    header: {
      studio: 'Tattoo Studio',
      nav: { portfolio: 'Portafolio', about: 'Sobre Mi', styles: 'Estilos' },
      book: 'Reservar Cita',
      openMenu: 'Abrir menu',
      closeMenu: 'Cerrar menu',
      languageLabel: 'Idioma',
    },
    hero: {
      location: 'Peñol, Antioquia',
      customTattoo: 'Tatuajes Personalizados',
      artist: 'Artista de Tatuajes',
      titleLine1: 'Tinta que se',
      titleLine2: 've de verdad.',
      subtitle:
        'Disenos personalizados con ejecucion limpia, sombreado solido y lectura fuerte en piel. Mira el trabajo primero y decide si este es tu estilo.',
      dm: 'DM en Instagram',
      form: 'Prefiero formulario',
      backgroundAlt: 'Artista tatuando en estudio con iluminacion tenue',
      recentLabel: 'Trabajo reciente',
      recentTitle: 'Detalle, contraste y curacion limpia.',
    },
    portfolio: {
      sectionLabel: 'Portafolio',
      titleLine1: 'Cada pieza,',
      titleLine2: 'una historia.',
      empty: 'No hay estilos disponibles todavia. Agregalos desde el panel admin.',
      seeMore: 'Ver mas trabajo en',
      divider: 'Juga Tattoo',
      close: 'Cerrar',
      previous: 'Anterior',
      next: 'Siguiente',
      viewPhoto: 'Ver foto',
      viewGallery: 'Ver galeria de',
      instagramAria: 'Ver @juga_tattoo en Instagram',
    },
    about: {
      sectionLabel: 'Sobre el Artista',
      titleLine1: 'Arte hecho',
      titleLine2: 'con intencion.',
      paragraph1:
        'Basado en Penol, Antioquia, juga_tattoo crea tatuajes personalizados con una linea artistica clara: realismo en tonalidades grises, retratos de mascotas, linea fina y acentos de color.',
      paragraph2:
        'Ya sea que estes buscando tu primer tatuaje o agregando a tu coleccion, juga_tattoo transforma tu vision en realidad, desde la primera consulta hasta el resultado final.',
      specialties: 'Especialidades',
      specialtiesList: [
        {
          name: 'Realismo en grises',
          description: 'Sombras, volumen y profundidad en composiciones black & grey.',
        },
        {
          name: 'Retratos de mascotas',
          description: 'Retratos fieles que capturan expresion y personalidad.',
        },
        {
          name: 'Linea fina',
          description: 'Trazos delicados, limpios y minimalistas con precision tecnica.',
        },
        {
          name: 'Color',
          description: 'Piezas con acentos vibrantes y contraste para mayor impacto visual.',
        },
      ],
      instagram: 'Ver mas en Instagram @juga_tattoo',
      cta: 'Solicitar Consulta',
    },
    stats: {
      sectionLabel: 'Numeros que Hablan',
      title: 'Arte que deja huella.',
      cards: [
        {
          value: '5+',
          label: 'Anos de Experiencia',
          description: 'Perfeccionando el arte del tatuaje en Colombia',
        },
        {
          value: '500+',
          label: 'Piezas Realizadas',
          description: 'Cada una unica, disenada para su portador',
        },
        {
          value: '100%',
          label: 'Diseno Personalizado',
          description: 'Sin disenos de catalogo, todo nace de tu idea',
        },
      ],
      prevReview: 'Resena anterior',
      nextReview: 'Siguiente resena',
      goToReview: 'Ir a resena',
      emptyReviews: 'Aun no hay resenas cargadas. Puedes agregarlas desde el panel admin.',
    },
    booking: {
      badge: 'DM por Instagram recomendado',
      titleLine1: 'Reserva tu',
      titleLine2: 'proxima pieza.',
      intro:
        'Si quieres respuesta mas rapida, escribeme por DM en Instagram. El formulario queda como alternativa para dejar tu solicitud completa.',
      trustItems: [
        'Consulta inicial gratuita',
        'Materiales 100% esteriles',
        'Diseno personalizado incluido',
        'Estudio en Penol, Antioquia',
      ],
      socialProof: '+500 clientes satisfechos',
      submittedTitle: 'Solicitud Enviada',
      submittedBody:
        'Gracias por contactarme. Te respondere dentro de las proximas 24 horas para confirmar los detalles de tu cita.',
      continueDm: 'Continuar por DM en @juga_tattoo',
      formTitle: 'Agenda tu tatuaje',
      formSubtitle:
        'El canal principal es Instagram DM. Si prefieres, tambien puedes dejar tu solicitud en el formulario.',
      dmButton: 'DM en Instagram',
      noInstagram:
        'No usas Instagram? Completa el formulario de abajo y te contacto por email o WhatsApp.',
      fullName: 'Nombre completo',
      fullNamePlaceholder: 'Tu nombre',
      email: 'Email',
      emailPlaceholder: 'tu@email.com',
      phone: 'Telefono',
      phonePlaceholder: '+57 300 000 0000',
      preferredDate: 'Fecha preferida',
      bodyPlacement: 'Zona del cuerpo',
      bodyPlacementPlaceholder: 'Selecciona una zona',
      tattooIdea: 'Idea / Descripcion del tatuaje',
      tattooIdeaPlaceholder: 'Describe tu idea, estilo preferido, tamano aproximado...',
      references: 'Referencias o inspiracion',
      optional: '(opcional)',
      referencesPlaceholder: 'Links de Instagram, Pinterest, o descripcion de referencia',
      sending: 'Enviando...',
      submit: 'Enviar Solicitud',
      consent: 'Al enviar, aceptas ser contactado por juga_tattoo para confirmar tu cita.',
      bodyPlacementError: 'Selecciona una zona del cuerpo.',
      submitError: 'No se pudo enviar la solicitud. Intentalo nuevamente.',
    },
    location: {
      location: 'Ubicacion',
      bookings: 'Citas',
      bookOnline: 'Reservar en linea',
    },
    footer: {
      location: 'Penol, Antioquia, Colombia',
      nav: { portfolio: 'Portafolio', about: 'Sobre Mi', book: 'Reservar' },
    },
  },
  en: {
    header: {
      studio: 'Tattoo Studio',
      nav: { portfolio: 'Portfolio', about: 'About', styles: 'Styles' },
      book: 'Book Session',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      languageLabel: 'Language',
    },
    hero: {
      location: 'Peñol, Antioquia',
      customTattoo: 'Custom Tattoo Art',
      artist: 'Tattoo Artist',
      titleLine1: 'Ink that',
      titleLine2: 'actually reads.',
      subtitle:
        'Custom designs with clean execution, strong shading, and clear readability on skin. Review the work first and decide if this is your style.',
      dm: 'DM on Instagram',
      form: 'Use booking form',
      backgroundAlt: 'Tattoo artist working in a dimly lit studio',
      recentLabel: 'Recent work',
      recentTitle: 'Detail, contrast, and clean healing.',
    },
    portfolio: {
      sectionLabel: 'Portfolio',
      titleLine1: 'Each piece,',
      titleLine2: 'a story.',
      empty: 'No styles available yet. Add them from the admin panel.',
      seeMore: 'See more work on',
      divider: 'Juga Tattoo',
      close: 'Close',
      previous: 'Previous',
      next: 'Next',
      viewPhoto: 'View photo',
      viewGallery: 'View gallery for',
      instagramAria: 'View @juga_tattoo on Instagram',
    },
    about: {
      sectionLabel: 'About the Artist',
      titleLine1: 'Art made',
      titleLine2: 'with intention.',
      paragraph1:
        'Based in Penol, Antioquia, juga_tattoo creates custom tattoos with a clear artistic line: black and grey realism, pet portraits, fine line, and bold color accents.',
      paragraph2:
        'Whether you are planning your first tattoo or adding to your collection, juga_tattoo turns your vision into reality from the first consultation to the final result.',
      specialties: 'Specialties',
      specialtiesList: [
        {
          name: 'Black and grey realism',
          description: 'Shadows, depth, and texture in high-impact black and grey pieces.',
        },
        {
          name: 'Pet portraits',
          description: 'Faithful portraits that capture personality and expression.',
        },
        {
          name: 'Fine line',
          description: 'Delicate, clean, and minimal work with precise technique.',
        },
        {
          name: 'Color',
          description: 'Pieces with vibrant accents and strong contrast for visual impact.',
        },
      ],
      instagram: 'See more on Instagram @juga_tattoo',
      cta: 'Request Consultation',
    },
    stats: {
      sectionLabel: 'Numbers That Speak',
      title: 'Art that leaves a mark.',
      cards: [
        {
          value: '5+',
          label: 'Years of Experience',
          description: 'Refining tattoo craftsmanship in Colombia',
        },
        {
          value: '500+',
          label: 'Pieces Completed',
          description: 'Each one unique and designed for its wearer',
        },
        {
          value: '100%',
          label: 'Custom Design',
          description: 'No catalog designs, every piece starts from your idea',
        },
      ],
      prevReview: 'Previous review',
      nextReview: 'Next review',
      goToReview: 'Go to review',
      emptyReviews: 'No reviews yet. You can add them from the admin panel.',
    },
    booking: {
      badge: 'Instagram DM recommended',
      titleLine1: 'Book your',
      titleLine2: 'next piece.',
      intro:
        'For the fastest response, message me on Instagram DM. The form is available as a full-request alternative.',
      trustItems: [
        'Free initial consultation',
        '100% sterile materials',
        'Custom design included',
        'Studio in Penol, Antioquia',
      ],
      socialProof: '500+ happy clients',
      submittedTitle: 'Request Sent',
      submittedBody:
        'Thanks for reaching out. I will reply within the next 24 hours to confirm your appointment details.',
      continueDm: 'Continue in DM at @juga_tattoo',
      formTitle: 'Book your tattoo',
      formSubtitle:
        'The primary channel is Instagram DM. If you prefer, you can also submit your request with this form.',
      dmButton: 'DM on Instagram',
      noInstagram:
        'Not using Instagram? Complete the form below and I will contact you by email or WhatsApp.',
      fullName: 'Full name',
      fullNamePlaceholder: 'Your name',
      email: 'Email',
      emailPlaceholder: 'your@email.com',
      phone: 'Phone',
      phonePlaceholder: '+1 555 000 0000',
      preferredDate: 'Preferred date',
      bodyPlacement: 'Body placement',
      bodyPlacementPlaceholder: 'Select a body area',
      tattooIdea: 'Tattoo idea / description',
      tattooIdeaPlaceholder: 'Describe your idea, preferred style, approximate size...',
      references: 'References or inspiration',
      optional: '(optional)',
      referencesPlaceholder: 'Instagram links, Pinterest links, or reference details',
      sending: 'Sending...',
      submit: 'Send Request',
      consent:
        'By submitting, you agree to be contacted by juga_tattoo to confirm your appointment.',
      bodyPlacementError: 'Please select a body area.',
      submitError: 'Your request could not be sent. Please try again.',
    },
    location: {
      location: 'Location',
      bookings: 'Bookings',
      bookOnline: 'Book online',
    },
    footer: {
      location: 'Penol, Antioquia, Colombia',
      nav: { portfolio: 'Portfolio', about: 'About', book: 'Book' },
    },
  },
};

export function translateStyleLabel(label: string, language: Language) {
  const normalized = label.trim();
  const match = STYLE_LABEL_TRANSLATIONS[normalized];
  return match ? match[language] : label;
}

export function translateStyleTag(tag: string, language: Language) {
  const normalized = tag.trim();
  const match = STYLE_TAG_TRANSLATIONS[normalized];
  return match ? match[language] : tag;
}

export function translateReviewQuote(quote: string, language: Language) {
  const normalized = quote.trim();
  const match = REVIEW_QUOTE_TRANSLATIONS[normalized];
  return match ? match[language] : quote;
}
